import React, { useContext, useEffect, useRef, useState } from 'react';
import { FiCrosshair, FiMapPin } from 'react-icons/fi';
import { MyContext } from '../Context/MyContext';
import { getCurrentPosition } from '../utils/geolocation';
import { resolveLocationDetails } from '../utils/reverseGeocode';
import AppIcon from './AppIcon';
import '../User/Pages/Components/froms/MapModal.css';
import './StreetMapPickerModal.css';

const StreetMapPickerModal = ({
  open,
  onClose,
  onConfirm,
  initialLat,
  initialLng,
  title = 'Change Location',
}) => {
  const { cities } = useContext(MyContext);
  const citiesRef = useRef(cities);
  citiesRef.current = cities;
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const pickedLatRef = useRef(initialLat || 20.5937);
  const pickedLngRef = useRef(initialLng || 78.9629);
  const addressRef = useRef('');
  const cityRef = useRef('');
  const cityIdRef = useRef(null);
  const [pickedLat, setPickedLat] = useState(initialLat || 20.5937);
  const [pickedLng, setPickedLng] = useState(initialLng || 78.9629);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const reverseGeo = async (lt, lg) => {
      setLoading(true);
      try {
        const details = await resolveLocationDetails(lt, lg, {}, citiesRef.current);
        setAddress(details.name);
        setCity(details.city || '');
        addressRef.current = details.name;
        cityRef.current = details.city || '';
        cityIdRef.current = details.cityId ?? null;
      } finally {
        setLoading(false);
      }
    };

    const initMap = () => {
      if (!mapDivRef.current || mapRef.current) return;
      const L = window.L;
      const lat = initialLat || 20.5937;
      const lng = initialLng || 78.9629;
      const map = L.map(mapDivRef.current, { zoomControl: true }).setView([lat, lng], initialLat ? 14 : 5);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(map);

      const icon = L.divIcon({
        className: '',
        html: '<div style="width:28px;height:28px;background:#0ea5e9;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.35);"></div>',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
      });

      const marker = L.marker([lat, lng], { draggable: true, icon }).addTo(map);
      markerRef.current = marker;
      mapRef.current = map;

      const updatePos = (latlng) => {
        pickedLatRef.current = latlng.lat;
        pickedLngRef.current = latlng.lng;
        setPickedLat(latlng.lat);
        setPickedLng(latlng.lng);
        reverseGeo(latlng.lat, latlng.lng);
      };

      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        updatePos(e.latlng);
      });
      marker.on('dragend', () => updatePos(marker.getLatLng()));
      reverseGeo(lat, lng);
    };

    if (window.L) initMap();
    else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [open, initialLat, initialLng]);

  const handleUseCurrentLocation = async () => {
    setGpsLoading(true);
    try {
      const pos = await getCurrentPosition();
      const details = await resolveLocationDetails(pos.lat, pos.lng, {}, citiesRef.current);
      pickedLatRef.current = pos.lat;
      pickedLngRef.current = pos.lng;
      addressRef.current = details.name;
      cityRef.current = details.city || '';
      cityIdRef.current = details.cityId ?? null;
      setPickedLat(pos.lat);
      setPickedLng(pos.lng);
      setAddress(details.name);
      setCity(details.city || '');
      if (markerRef.current && mapRef.current) {
        markerRef.current.setLatLng([pos.lat, pos.lng]);
        mapRef.current.setView([pos.lat, pos.lng], 15);
      }
    } catch (err) {
      setAddress(err.message || 'Could not get current location');
      setCity('');
    } finally {
      setGpsLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const details = await resolveLocationDetails(
        pickedLatRef.current,
        pickedLngRef.current,
        {
          name: addressRef.current || address,
          city: cityRef.current || city,
          cityId: cityIdRef.current,
        },
        citiesRef.current,
      );
      onConfirm({
        lat: pickedLatRef.current,
        lng: pickedLngRef.current,
        name: details.name,
        city: details.city,
        cityId: details.cityId ?? null,
        source: 'manual',
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="map-modal-overlay" onClick={onClose}>
      <div className="map-modal street-map-picker" onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <h3 className="map-modal-title">
            <FiMapPin size={18} /> {title}
          </h3>
          <button type="button" className="map-modal-close" onClick={onClose}>×</button>
        </div>
        <p className="map-modal-hint">Click the map or drag the pin to choose your search location.</p>
        <button
          type="button"
          className="street-map-gps-btn"
          onClick={handleUseCurrentLocation}
          disabled={gpsLoading}
        >
          <FiCrosshair size={16} />
          {gpsLoading ? 'Getting location…' : 'Use Current Location'}
        </button>
        <div ref={mapDivRef} className="map-leaflet-container" />
        <div className="map-modal-coords">
          <span className="map-coord-badge">
            {Number(pickedLat).toFixed(5)}, {Number(pickedLng).toFixed(5)}
          </span>
          {loading && <span className="map-coord-loading">Resolving location…</span>}
          {!loading && city && (
            <span className="map-city-preview">
              <FiMapPin size={12} aria-hidden />
              {city}
            </span>
          )}
          {!loading && address && <span className="map-address-preview">{address}</span>}
        </div>
        <div className="map-modal-footer">
          <button type="button" className="map-modal-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="map-modal-confirm" onClick={handleConfirm} disabled={loading}>
            <AppIcon name="check" size={14} /> {loading ? 'Resolving…' : 'Set Active Location'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StreetMapPickerModal;
